import { DateTimeSettingsEnum } from "../../enum/app-settings/AppSettingsEnum";
import { IHOPSettings } from "../../models/admin/IDimension";
import {
  IDateConfigValues,
  IKpiSettingsAPI,
  IKpiSettingsData,
  IKpiSettingsResponse,
} from "../../models/app-settings/AppSettingsModels";
import { TimeRange } from "../../models/common/DateTimeModels";
import {
  appSettingsTabsOther,
  appSettingsTabsYQB,
} from "./AppSettingsConstants";

export const transformHopSettingsData = (
  hopSettings: IHOPSettings,
  hopSettingsApiData: any
): any => {
  if (hopSettingsApiData?.length === 0) {
    return hopSettings;
  }

  const updatedHopSettings = hopSettings;

  const updateThresholds = (
    apiThresholds: any[],
    dimensionThresholds: any[]
  ) => {
    apiThresholds?.forEach((threshold: any) => {
      const dimension = dimensionThresholds.find(
        (d: any) => d.type === threshold.type
      );
      if (dimension) {
        dimension.earlyBuffer = threshold.earlyBuffer;
        dimension.lateBuffer = threshold.lateBuffer;
        const amberToRed = Number(threshold.amberToRed);
        const greenToAmber = Number(threshold.greenToAmber);
        dimension.poor.max = amberToRed - 1;
        dimension.moderate.min = amberToRed;
        dimension.moderate.max = greenToAmber - 1;
        dimension.green.min = greenToAmber;
      }
    });
  };

  // Update thresholds for each dimension
  updateThresholds(
    hopSettingsApiData[0],
    updatedHopSettings.dimensions[0].thresholds
  );
  updateThresholds(
    hopSettingsApiData[1],
    updatedHopSettings.dimensions[1].thresholds
  );
  updateThresholds(
    hopSettingsApiData[2],
    updatedHopSettings.dimensions[2].thresholds
  );
  updateThresholds(
    hopSettingsApiData[3],
    updatedHopSettings.dimensions[3].thresholds
  );

  return updatedHopSettings;
};

export const convertTimeStringToHourMinute = (
  time: string
): { hours: number; minutes: number } => {
  const [hours, minutes] = time.split(":").map(Number);
  return {
    hours: isNaN(hours) ? 0 : hours,
    minutes: isNaN(minutes) ? 0 : minutes,
  };
};

/**
 * Extracts a time range from a configuration item and returns it as a `TimeRange` object.
 * If the configuration item is invalid or missing required properties, a default time range
 * with all values set to 0 is returned.
 *
 * @param configItem - The configuration item containing `from` and `to` time strings in the format "HH:mm".
 * @returns A `TimeRange` object containing the parsed hours and minutes for `from` and `to` times.
 *
 * The returned object has the following structure:
 * - `fromHours`: The hour part of the `from` time.
 * - `fromMinutes`: The minute part of the `from` time.
 * - `toHours`: The hour part of the `to` time.
 * - `toMinutes`: The minute part of the `to` time.
 *
 * If the `from` or `to` time strings are invalid or missing, their respective values are set to 0.
 */
export const extractTimeRange = (configItem: any): TimeRange => {
  if (!configItem || !configItem.from || !configItem.to) {
    return {
      fromHours: 0,
      fromMinutes: 0,
      toHours: 0,
      toMinutes: 0,
    };
  }

  const from = convertTimeStringToHourMinute(configItem.from);
  const to = convertTimeStringToHourMinute(configItem.to);

  return {
    fromHours: from.hours,
    fromMinutes: from.minutes,
    toHours: to.hours,
    toMinutes: to.minutes,
  };
};

/**
 * Generates a configuration payload for date and time settings based on the provided values.
 *
 * @param payload - An object containing date and time configuration values.
 * @returns An object representing the configuration payload, including time settings and the selected configuration.
 *
 * The `timeSettings` array includes two objects:
 * - `RollingHrs`: Specifies the rolling hours and minutes range.
 * - `TimeDuration`: Specifies the duration hours and minutes range.
 *
 * Each object in `timeSettings` contains:
 * - `name`: The name of the time setting (e.g., `RollingHrs` or `TimeDuration`).
 * - `from`: The starting time in "HH:mm" format.
 * - `to`: The ending time in "HH:mm" format.
 *
 * The `selectedConfig` property represents the selected configuration value from the payload.
 */
export const getDateConfigPayload = (payload: IDateConfigValues): any => {
  return {
    timeSettings: [
      {
        name: DateTimeSettingsEnum.RollingHrs,
        from: `${payload.fromRollingHours}:${payload.fromRollingMinute}`,
        to: `${payload.toRollingHours}:${payload.toRollingMinute}`,
      },
      {
        name: DateTimeSettingsEnum.TimeDuration,
        from: `${payload.fromDurationHours}:${payload.fromDurationMinute}`,
        to: `${payload.toDurationHours}:${payload.toDurationMinute}`,
      },
    ],
    selectedConfig: payload.selectedConfig,
  };
};

export const convertKPIFetchData = (
  input: IKpiSettingsAPI[]
): IKpiSettingsData[] => {
  return input.map((item) => {
    const { hours: fromHour, minutes: fromMinute } =
      convertTimeStringToHourMinute(item.from);
    const { hours: toHour, minutes: toMinute } = convertTimeStringToHourMinute(
      item.to
    );

    return {
      id: item.name, // Assuming `name` is unique and can be used as an ID
      label: item.label,
      fromHour,
      fromMinute,
      toHour,
      toMinute,
      isRollingActive: item.isRollingActive,
      hidden: item.hidden,
      isError: false,
    };
  });
};

export const getKpiSettingsPayload = (
  kpiSettingsData: IKpiSettingsData[]
): IKpiSettingsResponse => {
  if (!kpiSettingsData || kpiSettingsData.length === 0) {
    return {
      kpiSettings: [],
    };
  }

  // Convert the kpiSettingsData to the required payload format
  const payloadData = kpiSettingsData.map((kpi) => {
    return {
      name: kpi.id,
      label: kpi.label,
      from: `${kpi.fromHour}:${kpi.fromMinute}`,
      to: `${kpi.toHour}:${kpi.toMinute}`,
      hidden: kpi.hidden,
      isRollingActive: kpi.isRollingActive,
    };
  });

  return {
    kpiSettings: payloadData,
  };
};

export const getAppSettingsTabs = (airport: string) => {
  if (airport === "YQB") {
    return appSettingsTabsYQB;
  } else {
    return [...appSettingsTabsYQB, ...appSettingsTabsOther];
  }
};
